import React, { JSX, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Admin from './pages/Admin';
import SupportDiagnostics from './pages/SupportDiagnostics';
import Layout from './components/Layout';
import ChangePassword from './pages/ChangePassword';
import PdfReportRoute from './pages/PdfReportRoute';
import OrgSelect from './pages/OrgSelect';
import StaffSelect from './pages/StaffSelect';
import SetupPin from './pages/SetupPin';
import SystemAdminLayout from './pages/SystemAdminLayout';
import Overview from './pages/systemadmin/Overview';
import WaitlistRegistrations from './pages/systemadmin/WaitlistRegistrations';
import Organisations from './pages/systemadmin/Organisations';
import OrganisationDetail from './pages/systemadmin/OrganisationDetail';
import PricingTiers from './pages/systemadmin/PricingTiers';
import Errors from './pages/systemadmin/Errors';
import ErrorGroupDetail from './pages/systemadmin/ErrorGroupDetail';
import ErrorOccurrence from './pages/systemadmin/ErrorOccurrence';
import SupportStaff from './pages/systemadmin/SupportStaff';
import { SystemAdminDataService } from './services/systemAdminDataService';
import './App.scss';
import { Auth } from 'aws-amplify';
import { AppStateProvider, useAppState } from './context/AppStateContext';
import { useIdleTimeout, idleLockApplies } from './hooks/useIdleTimeout';
import { useSessionExpiryGuard } from './hooks/useSessionExpiryGuard';
import { signOutAndRedirect } from './api/axios';
import { exitSupportSession } from './services/supportDataService';
import { UserDataService } from './services/userDataService';
import { decodeActiveUserToken } from './utils/staffUtils';
import { HelpProvider } from './help/HelpProvider';
import HelpWindow from './help/HelpWindow';
import './help/help.scss';
import PinKeypad from './components/PinKeypad';
import MfaSetupModal from './components/MfaSetupModal';
import { getSelectedOrgId } from './services/selectedOrg';
import OfflineRibbon from './components/connectivity/OfflineRibbon';

/**
 * Soft post-login nudge to enrol in two-factor auth while the Cognito pool is
 * still on "Optional". Shows once the user is in the app (active user set) if
 * their Cognito account has no TOTP factor. Dismissible for the session
 * ("Remind me later"); stops once enrolled. Retires itself when the pool is
 * later switched to Required (Cognito then forces enrolment at login).
 */
function MfaNudge() {
  const { activeUser, user, isLocked } = useAppState();
  const [show, setShow] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (!activeUser || !user) return;
    // MFA belongs to the Cognito (device-session) account, so only nudge when
    // the active user IS that signed-in device user — never prompt a
    // PIN-switched colleague to enrol someone else's account.
    const sameUser = !!activeUser.email && !!user.email &&
      activeUser.email.trim().toLowerCase() === user.email.trim().toLowerCase();
    if (!sameUser) return;
    if (sessionStorage.getItem('mfaNudgeDismissed') === '1') return;
    let cancelled = false;
    (async () => {
      try {
        const u = await Auth.currentAuthenticatedUser();
        const pref = await Auth.getPreferredMFA(u, { bypassCache: true });
        if (!cancelled && pref !== 'SOFTWARE_TOKEN_MFA') setShow(true);
      } catch { /* not signed in — no nudge */ }
    })();
    return () => { cancelled = true; };
  }, [activeUser?.id, user?.email]);

  const recheckAndClose = async () => {
    setShowSetup(false);
    try {
      const u = await Auth.currentAuthenticatedUser();
      const pref = await Auth.getPreferredMFA(u, { bypassCache: true });
      if (pref === 'SOFTWARE_TOKEN_MFA') setShow(false);
    } catch { /* ignore */ }
  };

  if (showSetup) return <MfaSetupModal onClose={recheckAndClose} />;
  if (!show || isLocked) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2050, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '2rem', minWidth: 360, maxWidth: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
        <h5 style={{ marginBottom: '0.5rem', fontWeight: 700 }}>Secure your account</h5>
        <p style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.45 }}>
          We're introducing two-factor authentication for extra security. We recommend setting it
          up now using an authenticator app on your phone — it only takes a minute, and it will
          become required soon. You can also set it up later from <strong>Account → Two-factor
          authentication</strong>.
        </p>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary"
            onClick={() => { sessionStorage.setItem('mfaNudgeDismissed', '1'); setShow(false); }}>
            Remind me later
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowSetup(true)}>
            Set up now
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * App-wide idle lock guard — rendered once inside the Router so it covers every page.
 * Fetches the org's configured timeout on mount and locks whenever a PIN user is active.
 */
function IdleLockGuard() {
  const { activeUser, setActiveUser, setActiveUserToken, idleTimeoutMs, setIdleTimeoutMs, isLocked, setIsLocked, selectedOrganisation, setSelectedOrganisation } = useAppState();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inAuthFlow = !idleLockApplies(pathname);

  // An active staff user is the condition, not a selected org: the API resolves
  // the org from the X-Organization-Id header (axios reads it from localStorage),
  // so this needs no org object in context. Staying off the auth screens is what
  // keeps a stale sessionStorage token from firing /security/organisation and 401ing.
  useEffect(() => {
    if (!activeUser || inAuthFlow) return;
    UserDataService.fetchOrgSettings()
      .then(org => setIdleTimeoutMs(org.inactivityTimeoutSeconds * 1000))
      .catch(() => {});
    // Keyed on the active user's id and the route, not the whole user object or
    // the context setters: neither changes anything about which org's settings
    // these are.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.id, inAuthFlow]);

  // Trade the stored org id back for the full record, app-wide. Only the id
  // survives a reload, and Home was the one place that rehydrated it — so
  // landing anywhere that isn't Home (a refresh on /admin, a bookmark, a help
  // step's deep link) left the org null in context for the whole visit.
  useEffect(() => {
    if (!activeUser || inAuthFlow || selectedOrganisation) return;
    const storedId = getSelectedOrgId();
    if (!storedId) return;
    UserDataService.fetchOrganisations()
      .then(orgs => {
        const match = orgs.find(o => o.id === parseInt(storedId, 10));
        if (match) setSelectedOrganisation(match);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser?.id, inAuthFlow, selectedOrganisation]);

  // Armed on the active PIN user alone. It used to also require the org object
  // in context, which meant that on the pages not rendered by Home — /admin,
  // /change-password — the timer silently never armed and an unattended device
  // stayed open. A safeguarding lock cannot depend on an unrelated fetch.
  // Support has no PIN to unlock with: idle ends the session instead of locking.
  useIdleTimeout(
    () => (activeUser?.isSupport ? exitSupportSession() : setIsLocked(true)),
    idleTimeoutMs,
    !isLocked && !!activeUser && !inAuthFlow,
  );

  if (!isLocked || !activeUser || activeUser.isSupport) return null;

  return (
    <PinKeypad
      staffName={activeUser.name}
      staffInitials={activeUser.initials}
      staffColour={activeUser.colourCode}
      onSubmit={async (pin) => {
        const result = await UserDataService.verifyPin(activeUser.id, pin);
        sessionStorage.setItem('activeUserToken', result.activeUserToken);
        setActiveUserToken(result.activeUserToken);
        setActiveUser(result.activeUser);
        setIsLocked(false);
      }}
      onCancel={() => { setIsLocked(false); navigate('/staff-select'); }}
      onLockout={async () => {
        setIsLocked(false);
        setActiveUser(null);
        setActiveUserToken(null);
        sessionStorage.removeItem('activeUserToken');
        await Auth.signOut();
        navigate('/login');
      }}
      cancelLabel="Switch user"
    />
  );
}

/**
 * Proactively signs the user out to the login screen when their Cognito
 * session can no longer be refreshed — via an expiry timer and a tab
 * focus/visibility re-check — instead of waiting for the next API call to 401.
 * Scoped to a signed-in active user (the auth screens handle their own checks).
 */
function SessionExpiryGuard() {
  const { activeUser } = useAppState();
  useSessionExpiryGuard(!!activeUser, signOutAndRedirect);
  return null;
}

/**
 * The offline ribbon for screens outside the app chrome (login, PIN, workspace
 * picker). Stands down by itself wherever Layout / SystemAdminLayout show their
 * own; never on the PDF render route, which is captured to a file.
 */
function AuthScreenOfflineRibbon() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/pdf-report')) return null;
  return <OfflineRibbon placement="floating" />;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const [authState, setAuthState] = useState<'checking' | 'signedIn' | 'signedOut'>('checking');

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => setAuthState('signedIn'))
      .catch(() => setAuthState('signedOut'));
  }, []);

  if (authState === 'checking') return <div>Loading...</div>;

  return authState === 'signedIn' ? children : <Navigate to="/login" replace />;
}

/**
 * Requires an organisation to have been selected in this tab (services/selectedOrg).
 * Redirects to /select-org if not — this covers direct-link access and page refreshes.
 */
function OrgGuard({ children }: { children: JSX.Element }) {
  const selectedOrgId = getSelectedOrgId();
  if (!selectedOrgId) {
    return <Navigate to="/select-org" replace />;
  }
  return children;
}

/**
 * Requires an active user token (PIN-authenticated staff member) stored in sessionStorage.
 * Redirects to /staff-select if not present — this ensures a staff member is always selected.
 */
function StaffGuard({ children }: { children: JSX.Element }) {
  // Require a PIN token that's actually still valid — not merely present.
  // An expired token is silently ignored by the API (no 401), so letting it
  // through would drop the user onto a page where every privileged action
  // 403s. decodeActiveUserToken returns null for absent/malformed/expired.
  const activeUserToken = sessionStorage.getItem('activeUserToken');
  if (!activeUserToken || !decodeActiveUserToken(activeUserToken)) {
    return <Navigate to="/staff-select" replace />;
  }
  return children;
}

/**
 * Gates the cross-org System Administrator area. Confirms the authenticated
 * Cognito user is on the backend's system-admin allow-list; non-admins (and
 * any check failure) are sent to the workspace picker. Sits outside
 * OrgGuard/StaffGuard — this area has no organisation or PIN context.
 */
function SystemAdminGuard({ children }: { children: JSX.Element }) {
  const [state, setState] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    SystemAdminDataService.checkSystemAdmin()
      .then(isAdmin => setState(isAdmin ? 'allowed' : 'denied'))
      .catch(() => setState('denied'));
  }, []);

  if (state === 'checking') return <div>Loading...</div>;
  return state === 'allowed' ? children : <Navigate to="/select-org" replace />;
}

function App() {
  return (
    <AppStateProvider>
      <Router>
        <IdleLockGuard />
        <SessionExpiryGuard />
        <MfaNudge />
        <AuthScreenOfflineRibbon />
        {/* Help sits inside the Router (its steps navigate) but outside
            <Routes>, so a running How To survives moving between pages. */}
        <HelpProvider>
        <HelpWindow />
        <Routes>
          <Route path="/pdf-report" element={<PdfReportRoute />} />
          <Route path="/login" element={<Login />} />

          {/* First-time PIN setup — requires auth only, no org needed */}
          <Route path="/setup-pin" element={
            <ProtectedRoute>
              <SetupPin />
            </ProtectedRoute>
          } />

          {/* Workspace selection — requires auth, no org needed yet */}
          <Route path="/select-org" element={
            <ProtectedRoute>
              <OrgSelect />
            </ProtectedRoute>
          } />

          {/* System Administrator area — requires auth + system-admin allow-list.
              No org or PIN context (sits outside OrgGuard/StaffGuard). */}
          <Route path="/system-admin" element={
            <ProtectedRoute>
              <SystemAdminGuard>
                <SystemAdminLayout />
              </SystemAdminGuard>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/system-admin/overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="waitlist" element={<WaitlistRegistrations />} />
            <Route path="organisations" element={<Organisations />} />
            <Route path="organisations/:orgId" element={<OrganisationDetail />} />
            <Route path="pricing" element={<PricingTiers />} />
            <Route path="errors" element={<Errors />} />
            <Route path="errors/occurrences/:occurrenceId" element={<ErrorOccurrence />} />
            <Route path="errors/:groupId" element={<ErrorGroupDetail />} />
            <Route path="support-staff" element={<SupportStaff />} />
          </Route>

          {/* Staff profile selection — requires auth + org selection */}
          <Route path="/staff-select" element={
            <ProtectedRoute>
              <OrgGuard>
                <StaffSelect />
              </OrgGuard>
            </ProtectedRoute>
          } />

          {/* Main app — requires auth + org selection + active staff user */}
          <Route element={
            <ProtectedRoute>
              <OrgGuard>
                <StaffGuard>
                  <Layout />
                </StaffGuard>
              </OrgGuard>
            </ProtectedRoute>
          }>
            <Route path="/" element={<Home />} />
            <Route path="/posts" element={<Home />} />
            <Route path="/schedule" element={<Home />} />
            <Route path="/attendance" element={<Home />} />
            <Route path="/attendance-stats" element={<Home />} />
            <Route path="/insights" element={<Home />} />
            <Route path="/reports" element={<Home />} />
            <Route path="/documents" element={<Home />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/support-diagnostics" element={<SupportDiagnostics />} />
          </Route>
          <Route path="/change-password" element={
            <ProtectedRoute>
              <OrgGuard>
                <StaffGuard>
                  <ChangePassword />
                </StaffGuard>
              </OrgGuard>
            </ProtectedRoute>
          } />
        </Routes>
        </HelpProvider>
      </Router>
    </AppStateProvider>
  );
}

export default App;
